import { useCallback, useEffect, useState } from 'react';
import {
  revenueopsApi,
  type BudgetRecord,
  type CampaignRecord,
  type KeywordRecord,
  type OpportunityRecord,
  type RevenueForecastRecord,
  type RevenueOpsDashboard,
  type RevenuePlanRecord,
  type RevenueRecommendation,
  type SearchTermRecord,
} from '../../api/revenueops';

type Tab = 'dashboard' | 'campaigns' | 'keywords' | 'search-terms' | 'budgets' | 'forecasts' | 'plans' | 'opportunities' | 'recommendations';

function statusClass(s: string) {
  if (s === 'enabled' || s === 'approved' || s === 'active' || s === 'completed') return 'text-emerald-700 bg-emerald-50';
  if (s === 'paused' || s === 'draft' || s === 'at_risk' || s === 'investigating') return 'text-amber-700 bg-amber-50';
  if (s === 'archived' || s === 'failed' || s === 'open') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function priorityClass(p?: string) {
  if (p === 'critical' || p === 'high') return 'text-red-700 bg-red-50';
  if (p === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

function fmtCurrency(n: number) {
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

function fmtNum(n: number) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function campaignTypeLabel(t: string) {
  return t.replace(/_/g, ' ');
}

export function RevenueOpsPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<RevenueOpsDashboard | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [keywords, setKeywords] = useState<KeywordRecord[]>([]);
  const [searchTerms, setSearchTerms] = useState<SearchTermRecord[]>([]);
  const [budgets, setBudgets] = useState<BudgetRecord[]>([]);
  const [forecasts, setForecasts] = useState<RevenueForecastRecord[]>([]);
  const [plans, setPlans] = useState<RevenuePlanRecord[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityRecord[]>([]);
  const [recommendations, setRecommendations] = useState<RevenueRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, camp, kw, st, bud, fcst, pln, opp, recs] = await Promise.all([
        revenueopsApi.dashboard(), revenueopsApi.listCampaigns(), revenueopsApi.listKeywords(),
        revenueopsApi.listSearchTerms(), revenueopsApi.listBudgets(), revenueopsApi.listForecasts(),
        revenueopsApi.listRevenuePlans(), revenueopsApi.listOpportunities(), revenueopsApi.listRecommendations(),
      ]);
      setDashboard(dash); setCampaigns(camp); setKeywords(kw); setSearchTerms(st);
      setBudgets(bud); setForecasts(fcst); setPlans(pln); setOpportunities(opp); setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load revenue operations data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await revenueopsApi.generateRecommendations(); await refresh(); } finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Revenue Dashboard' },
    { id: 'campaigns', label: 'Advertising Console' },
    { id: 'keywords', label: 'Keyword Explorer' },
    { id: 'search-terms', label: 'Search Terms' },
    { id: 'budgets', label: 'Budget Planner' },
    { id: 'forecasts', label: 'Forecast Center' },
    { id: 'plans', label: 'Revenue Planner' },
    { id: 'opportunities', label: 'Opportunity Center' },
    { id: 'recommendations', label: 'AI Recommendations' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Revenue Growth &amp; Advertising Command Center</h1>
          <p className="text-sm text-slate-500">Campaign management, bidding intelligence, revenue planning &amp; AI revenue copilot — APP-00010</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">Refresh</button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Active Campaigns', value: `${dashboard.active_campaigns}/${dashboard.total_campaigns}` },
            { label: 'Ad Spend', value: fmtCurrency(dashboard.total_spend) },
            { label: 'Ad Sales', value: fmtCurrency(dashboard.total_sales) },
            { label: 'Blended ROAS', value: dashboard.blended_roas.toFixed(2) },
            { label: 'Blended ACOS', value: `${dashboard.blended_acos.toFixed(1)}%` },
            { label: 'TACOS', value: `${dashboard.tacos.toFixed(1)}%` },
            { label: 'Organic Revenue', value: `${dashboard.organic_revenue_pct.toFixed(1)}%` },
            { label: 'Revenue Gap', value: `${dashboard.revenue_gap_pct.toFixed(1)}%` },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'campaigns' && (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-900">{c.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(c.status)}`}>{c.status}</span>
                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs text-violet-700">{campaignTypeLabel(c.type)}</span>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-4 text-sm text-slate-600">
                <span>Spend: {fmtCurrency(c.spend)}</span>
                <span>Sales: {fmtCurrency(c.sales)}</span>
                <span>ROAS: {c.roas.toFixed(2)}</span>
                <span>ACOS: {c.acos.toFixed(1)}%</span>
                <span>Impressions: {fmtNum(c.impressions)}</span>
                <span>Clicks: {fmtNum(c.clicks)}</span>
                <span>CTR: {c.ctr.toFixed(2)}%</span>
                <span>NTB: {c.ntb_percent.toFixed(1)}%</span>
              </div>
              {c.portfolio && <p className="mt-1 text-xs text-slate-400">Portfolio: {c.portfolio} · Budget: {fmtCurrency(c.daily_budget)}/day</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'keywords' && (
        <div className="space-y-3">
          {keywords.map((k) => (
            <div key={k.id} className={`rounded-xl border bg-white p-4 shadow-sm ${k.is_negative ? 'border-red-200' : 'border-slate-200'}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{k.keyword}</span>
                {k.match_type && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{k.match_type}</span>}
                {k.is_negative && <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">negative</span>}
                {!k.is_negative && <span className="text-sm text-slate-500">Bid: ${k.bid.toFixed(2)}</span>}
              </div>
              {!k.is_negative && (
                <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                  <span>Orders: {k.orders}</span>
                  <span>Sales: {fmtCurrency(k.sales)}</span>
                  <span>ACOS: {k.acos.toFixed(1)}%</span>
                  <span>CTR: {k.ctr.toFixed(2)}%</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'search-terms' && (
        <div className="space-y-3">
          {searchTerms.map((st) => (
            <div key={st.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{st.search_term}</span>
                {st.organic_rank != null && st.organic_rank > 0 && (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">Organic #{st.organic_rank}</span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                <span>Orders: {st.orders}</span>
                <span>Sales: {fmtCurrency(st.sales)}</span>
                <span>ACOS: {st.acos.toFixed(1)}%</span>
                <span>Clicks: {st.clicks}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'budgets' && (
        <div className="space-y-3">
          {budgets.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{b.portfolio}</span>
                <span className="text-sm text-slate-500">{b.utilization_pct.toFixed(1)}% utilized</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                <span>Allocated: {fmtCurrency(b.allocated_budget)}</span>
                <span>Spent: {fmtCurrency(b.actual_spend)}</span>
                {b.recommended_adjustment !== 0 && (
                  <span className={b.recommended_adjustment > 0 ? 'text-emerald-600' : 'text-red-600'}>
                    Rec. adj: {b.recommended_adjustment > 0 ? '+' : ''}{fmtCurrency(b.recommended_adjustment)}
                  </span>
                )}
              </div>
              {b.rule && <p className="mt-1 text-xs text-slate-400">{b.rule}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'forecasts' && (
        <div className="space-y-3">
          {forecasts.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(f.status)}`}>{f.status}</span>
                <span className="text-lg font-semibold text-emerald-700">{fmtCurrency(f.revenue)}</span>
                {f.period && <span className="text-sm text-slate-500">{f.period}</span>}
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-4 text-sm text-slate-600">
                <span>Orders: {fmtNum(f.orders)}</span>
                <span>Ad Spend: {fmtCurrency(f.ad_spend)}</span>
                <span>ROAS: {f.roas.toFixed(2)}</span>
                <span>Organic Lift: +{f.organic_lift_pct.toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'plans' && (
        <div className="space-y-3">
          {plans.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium capitalize">{p.period} Plan</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(p.status)}`}>{p.status.replace(/_/g, ' ')}</span>
                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs text-violet-700">{p.goal.replace(/_/g, ' ')}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                <span>Target: {fmtCurrency(p.target_revenue)}</span>
                <span>Actual: {fmtCurrency(p.actual_revenue)}</span>
                <span className={p.gap_pct < 0 ? 'text-red-600' : 'text-emerald-600'}>Gap: {p.gap_pct.toFixed(1)}%</span>
              </div>
              {p.roadmap && <p className="mt-2 text-sm text-slate-500">{p.roadmap}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'opportunities' && (
        <div className="space-y-3">
          {opportunities.map((o) => (
            <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${priorityClass(o.priority)}`}>{o.priority}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(o.status)}`}>{o.status}</span>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs text-sky-700">{o.category.replace(/_/g, ' ')}</span>
              </div>
              <h3 className="mt-1 font-medium text-slate-900">{o.title}</h3>
              {o.description && <p className="mt-1 text-sm text-slate-600">{o.description}</p>}
              {o.impact && <p className="mt-1 text-sm font-medium text-emerald-700">{o.impact}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={generateRecs} disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">#{r.priority_rank}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                <span>{r.type.replace(/_/g, ' ')}</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.expected_impact && <span>Impact: {r.expected_impact}</span>}
                {r.requires_approval && <span className="text-amber-600">Requires approval</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
